/**
 * SourceCVSelect — RC 1.19
 *
 * Grouped dropdown listing the user's uploaded CVs + generated CV
 * variants. Used on CV Variants, Cover Letters, and Job Detail
 * pages.
 *
 * Value format: `${source_type}:${source_id}` (e.g.
 * `uploaded_cv:abc-123` or `cv_variant:def-456`). This encoding
 * keeps the parent `<select>` native and testable by data-testid.
 *
 * Props:
 *   uploadedCvs    — [{ id, original_filename, is_primary }]
 *   variants       — [{ id, variant_name }]
 *   value          — "<type>:<id>"
 *   onChange       — (newValue, {source_type, source_id}) => void
 *   defaultToPrimary — when true and value is empty, initialise to the primary CV
 *   testId         — data-testid root
 */
import React, { useEffect, useMemo } from 'react';

export const encodeSource = (t, i) => (t && i ? `${t}:${i}` : '');
export const decodeSource = (v) => {
  if (!v) return { source_type: null, source_id: null };
  const [t, ...rest] = v.split(':');
  return { source_type: t || null, source_id: rest.join(':') || null };
};

export const SourceCVSelect = ({
  uploadedCvs = [],
  variants = [],
  value = '',
  onChange,
  defaultToPrimary = true,
  testId = 'source-cv-select',
  includeVariants = true,
  disabled = false,
}) => {
  const primary = useMemo(
    () => (uploadedCvs || []).find((cv) => cv.is_primary) || uploadedCvs?.[0] || null,
    [uploadedCvs],
  );

  // Auto-seed value to Primary uploaded CV if nothing selected.
  useEffect(() => {
    if (!value && defaultToPrimary && primary) {
      const v = encodeSource('uploaded_cv', primary.id);
      onChange?.(v, { source_type: 'uploaded_cv', source_id: primary.id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary?.id, defaultToPrimary]);

  const handle = (e) => {
    const v = e.target.value;
    onChange?.(v, decodeSource(v));
  };

  return (
    <select
      value={value}
      onChange={handle}
      disabled={disabled}
      data-testid={testId}
      className="w-full border border-ndor-edge rounded-md px-3 py-2 text-sm bg-ndor-surface-alt text-ndor-ink focus:outline-none focus:ring-2 focus:ring-ndor-accent/40"
    >
      <option value="" disabled>— Choose a Source CV —</option>

      {uploadedCvs && uploadedCvs.length > 0 && (
        <optgroup label="Uploaded CVs">
          {uploadedCvs.map((cv) => (
            <option
              key={`uploaded-${cv.id}`}
              value={encodeSource('uploaded_cv', cv.id)}
              data-testid={`${testId}-uploaded-${cv.id}`}
            >
              {cv.original_filename || 'Uploaded CV'}
              {cv.is_primary ? ' — Primary' : ''}
            </option>
          ))}
        </optgroup>
      )}

      {includeVariants && variants && variants.length > 0 && (
        <optgroup label="Generated CV Variants">
          {variants.map((v) => (
            <option
              key={`variant-${v.id}`}
              value={encodeSource('cv_variant', v.id)}
              data-testid={`${testId}-variant-${v.id}`}
            >
              {`Variant — ${v.variant_name || v.id.slice(0, 6)}`}
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
};

export default SourceCVSelect;
